import type { UserPayload } from '@Application/Users/Users/Actions/usersFetched';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `roleId`, `facilityIds` and `organizationIds` are derived from the payload's `{ id }`-only
 * relations: the server never sends a label, so no getter here fabricates one — the page resolves
 * labels from the role, organization and facility lists it already loads. `organizationIds` is what
 * the edit form reads as "the organizations this user already carries" — the other half of the
 * union it must send back alongside the organizations of the facilities newly checked.
 */
class UserView {
	readonly id: number;
	readonly email: string;
	readonly firstName: string;
	readonly lastName: string;
	readonly isActive: boolean;
	readonly status: UserPayload['status'];
	readonly invitedAt: string | null;
	readonly activatedAt: string | null;
	readonly invitationExpiresAt: string | null;
	readonly hasMfaEnabled: boolean;
	readonly roleId: number | null;
	readonly facilityIds: readonly number[];
	readonly organizationIds: readonly number[];
	readonly authenticationMode: UserPayload['authenticationMode'];
	readonly ssoLinkedAt: string | null;

	constructor(payload: UserPayload) {
		this.id = payload.id;
		this.email = payload.email;
		this.firstName = payload.firstName;
		this.lastName = payload.lastName;
		this.isActive = payload.isActive;
		this.status = payload.status;
		this.invitedAt = payload.invitedAt;
		this.activatedAt = payload.activatedAt;
		this.invitationExpiresAt = payload.invitationExpiresAt;
		this.hasMfaEnabled = payload.hasMfaEnabled;
		this.roleId = payload.role !== null ? payload.role.id : null;
		this.facilityIds = payload.facilities.map((facility) => facility.id);
		this.organizationIds = payload.organizations.map((organization) => organization.id);
		this.authenticationMode = payload.authenticationMode;
		this.ssoLinkedAt = payload.ssoLinkedAt;

		Object.freeze(this);
	}

	get fullName(): string {
		return `${this.firstName} ${this.lastName}`;
	}

	get isPendingInvitation(): boolean {
		return this.status.value === 'invited';
	}

	get isNeverActivated(): boolean {
		return this.activatedAt === null;
	}

	get isInvitationExpired(): boolean {
		return this.invitationExpiresAt !== null && new Date(this.invitationExpiresAt) < new Date();
	}
}

export default UserView;
